<template>
    <div class="user-portfolio">
        <div class="user-portfolio__wrapper" :style="{ 'min-height': `${contentHeight}px` }">
            <transition name="fade">
                <div v-if="loading" class="user-portfolio__loader">
                    <Loader />
                </div>
            </transition>
            <transition name="fade">
                <div v-if="!loading" class="user-portfolio__content">
                    <div v-if="$route.query.offset" class="user-portfolio__back">
                        <router-link
                            class="user-portfolio__back-link"
                            :to="{
                                name: backRouterName,
                                query: backQuery,
                            }"
                        >
                            Поиск пользователей
                        </router-link>
                    </div>
                    <div class="title">
                        <div class="title__block">
                            <h1 class="title__text">Информация о пользователе</h1>
                            <div class="title__time" v-if="lastUserActivityTime !== null">
                                Последняя активность:
                                <br v-if="isMobile" />
                                <span>{{ lastUserActivityTime | fullIsoDate }}</span>
                            </div>
                            <div class="title__download" v-loading.small="!getCanDownload">
                                <a
                                    v-if="getCanDownload"
                                    ref="download"
                                    href="#"
                                    class="download"
                                    @click="showPopper = !showPopper"
                                    >Скачать</a
                                >
                            </div>
                        </div>
                        <div v-if="$route.query.offset" class="title__block">
                            <router-link
                                v-if="getPrevUserLink"
                                class="portfolio-link portfolio-link--prev"
                                :to="getPrevUserLink"
                            >
                                Предыдущее портфолио
                            </router-link>
                            <router-link
                                v-if="getNextUserLink"
                                class="portfolio-link portfolio-link--next"
                                :to="getNextUserLink"
                            >
                                Следующее портфолио
                            </router-link>
                        </div>
                    </div>

                    <div class="user-portfolio-columns">
                        <div class="user-portfolio-column user-portfolio-column--left">
                            <PortfolioPersonalData
                                class="user-portfolio-block"
                                :highlights="getCurrentUserSearchHighlights"
                            />
                            <PortfolioWorkState class="user-portfolio-block" />

                            <template v-if="hasUserContactsAccess">
                                <div
                                    v-if="hasContacts && hasSocials"
                                    class="user-portfolio-columns user-portfolio-columns--inner"
                                >
                                    <PortfolioContacts
                                        class="user-portfolio-block user-portfolio-block--blue"
                                        :highlights="getCurrentUserSearchHighlights"
                                    />
                                    <PortfolioSocials class="user-portfolio-block user-portfolio-block--blue" />
                                </div>
                                <PortfolioContacts
                                    v-else-if="hasContacts"
                                    class="user-portfolio-block user-portfolio-block--blue"
                                    :highlights="getCurrentUserSearchHighlights"
                                />
                                <PortfolioSocials
                                    v-else-if="hasSocials"
                                    class="user-portfolio-block user-portfolio-block--blue"
                                />
                            </template>

                            <PortfolioCareer
                                class="user-portfolio-block"
                                :highlights="getCurrentUserSearchHighlights"
                            />
                            <PortfolioEducation
                                class="user-portfolio-block"
                                :highlights="getCurrentUserSearchHighlights"
                            />
                            <PortfolioAdditional
                                class="user-portfolio-block user-portfolio-block--blue user-portfolio-block--wide-padding"
                            />
                            <PortfolioComments
                                v-if="!isMobile"
                                class="user-portfolio-block user-portfolio-block--borderless"
                                :highlights="getCurrentUserSearchHighlights"
                            />
                        </div>
                        <div class="user-portfolio-column user-portfolio-column--right">
                            <PortfolioProjectParticipation class="user-portfolio-block" />
                            <PortfolioEventParticipation class="user-portfolio-block" />
                            <PortfolioAchievements class="user-portfolio-block" />
                            <PortfolioPersonalReports class="user-portfolio-block" />
                            <PortfolioCompetencies class="user-portfolio-block" />
                            <PortfolioCourses class="user-portfolio-block" />
                            <PortfolioInternShips class="user-portfolio-block" />
                            <PortfolioVacancies class="user-portfolio-block" />
                            <PortfolioComments
                                v-if="isMobile"
                                class="user-portfolio-block user-portfolio-block--borderless"
                                :highlights="getCurrentUserSearchHighlights"
                            />
                        </div>
                    </div>
                </div>
            </transition>
        </div>

        <div v-if="showPopper" id="menuPopper" ref="menuPopper" class="user-portfolio__menu">
            <div v-loading.small="downloading.docx" @click="download('docx')">Скачать word</div>
            <div v-loading.small="downloading.pdf" @click="download('pdf')">Скачать pdf</div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Loader from '@/components/UI/Loader.vue';
import PortfolioAchievements from '@/components/portfolio/PortfolioAchievements.vue';
import PortfolioAdditional from '@/components/portfolio/PortfolioAdditional.vue';
import PortfolioCareer from '@/components/portfolio/PortfolioCareer.vue';
import PortfolioComments from '@/components/portfolio/comments/PortfolioComments.vue';
import PortfolioCourses from '@/components/portfolio/courses/PortfolioCourses.vue';
import PortfolioEducation from '@/components/portfolio/PortfolioEducation.vue';
import PortfolioEventParticipation from '@/components/portfolio/PortfolioEventParticipation.vue';
import PortfolioPersonalData from '@/components/portfolio/PortfolioPersonalData.vue';
import PortfolioPersonalReports from '@/components/portfolio/PortfolioPersonalReports.vue';
import PortfolioProjectParticipation from '@/components/portfolio/projects/PortfolioProjectParticipation.vue';
import PortfolioCompetencies from '@/components/portfolio/PortfolioCompetencies.vue';
import PortfolioWorkState from '@/components/portfolio/PortfolioWorkState.vue';
import PortfolioContacts from '@/components/portfolio/PortfolioContacts.vue';
import PortfolioSocials from '@/components/portfolio/PortfolioSocials.vue';
import PortfolioInternShips from '@/components/portfolio/internships/PortfolioInternShips.vue';
import PortfolioVacancies from '@/components/portfolio/vacancies/PortfolioVacancies.vue';
import { graphQueryConstructor, lexParse } from '@/tools/graphQueryConstructor';
import gql from 'graphql-tag';
import qs from 'qs';
import { createPopper } from '@popperjs/core';
import { getUniqueHighlights } from '@/tools/graphQueryHelpers';

export default {
    components: {
        Loader,

        PortfolioPersonalData,
        PortfolioWorkState,
        PortfolioContacts,
        PortfolioSocials,
        PortfolioCareer,
        PortfolioEducation,
        PortfolioAdditional,
        PortfolioComments,

        PortfolioProjectParticipation,
        PortfolioEventParticipation,
        PortfolioAchievements,
        PortfolioPersonalReports,
        PortfolioCompetencies,
        PortfolioCourses,
        PortfolioInternShips,
        PortfolioVacancies,
    },

    data: () => ({
        loading: false,
        downloading: {
            docx: false,
            pdf: false,
        },
        searchString: '',
        filters: {},
        skip: 0,
        offset: 0,
        showPopper: false,
        popper: null,
        lastUserActivityTime: '',
    }),

    apollo: {
        $skipAll: true,
        users: {
            query() {
                let searchData;

                try {
                    searchData = lexParse(this.searchString);
                } catch (e) {
                    searchData = this.searchString;
                }

                return gql(graphQueryConstructor(this.filters, searchData));
            },
            variables: {
                searchString: '',
                skip: 0,
            },
        },
    },

    async created() {
        if (!this.hasUserSearchAccess) {
            this.$router.push({ name: '404' });
        } else {
            await this.initPage();
        }
    },

    watch: {
        async userID() {
            await this.initPage();
        },
        showPopper() {
            if (this.showPopper) {
                this.$nextTick(() => {
                    const elem = this.$refs.download;
                    const menu = this.$refs.menuPopper;
                    this.popper = createPopper(elem, menu, {
                        placement: 'bottom',
                        modifiers: [
                            {
                                name: 'offset',
                                options: {
                                    offset: [20, 10],
                                },
                            },
                        ],
                    });
                });
            } else {
                if (this.popper) {
                    this.popper.destroy();
                }
            }
        },
    },

    computed: {
        ...mapGetters(['contentHeight', 'isMobile']),
        ...mapGetters('auth', ['hasUserSearchAccess', 'hasUserContactsAccess']),
        ...mapGetters('newUsers', ['getUser', 'getUserIsAFriend', 'getCanDownload']),

        userID() {
            return parseInt(this.$route.params.id || '0');
        },

        title() {
            const user = this.getUser;

            return user ? [user.surname, user.name, user.patronymic].filter((v) => !!v).join(' ') : 'Имя неизвестно';
        },

        getCurrentUserSearch() {
            for (const user of (this.users && this.users.items) || []) {
                if (user.id === this.userID) {
                    return user;
                }
            }

            return null;
        },

        getCurrentUserSearchHighlights() {
            const user = this.getCurrentUserSearch;

            return user ? getUniqueHighlights(user.highlights) : null;
        },

        getPrevUserLink() {
            if (this.offset === 0) {
                return null;
            }

            let lastUserId = null;

            for (const user of (this.users && this.users.items) || []) {
                if (user.id === this.userID) {
                    break;
                }
                lastUserId = user.id;
            }

            if (!lastUserId) {
                return null;
            }

            const query = { ...this.$route.query };
            query.offset = this.offset - 1;

            return this.$router.resolve({
                name: this.$route.name,
                query,
                params: { id: lastUserId },
            }).href;
        },
        getNextUserLink() {
            let nextUserId = null;

            for (const user of (this.users && this.users.items) || []) {
                if (nextUserId === this.userID) {
                    nextUserId = user.id;
                    break;
                }
                nextUserId = user.id;
            }

            if (!nextUserId || nextUserId === this.userID) {
                return null;
            }

            const query = { ...this.$route.query };
            query.offset = this.offset + 1;

            return this.$router.resolve({
                name: this.$route.name,
                query,
                params: { id: nextUserId },
            }).href;
        },
        backRouterName() {
            if (this.$route.query['filters[favorite_membership][isFavorite]'] === 'true') {
                return 'favorites';
            } else {
                return 'account-user-search';
            }
        },
        backQuery() {
            const query = { ...this.$route.query };

            delete query.offset;

            return query;
        },

        hasContacts() {
            return ['tel', 'regTel', 'email', 'regEmail'].find((v) => this.getUser[v]);
        },

        hasSocials() {
            return ['telegram', 'vk', 'ok', 'zen', 'yt', 'rutube', 'tiktok', 'yappy', 'tenchat'].find(
                (v) => this.getUser[v]
            );
        },
    },

    methods: {
        ...mapActions('newUsers', [
            'fullResetUserStore',
            'loadFullUserById',
            'loadActivityTimeUserById',
            'checkFriend',
            'downloadPdf',
            'downloadDocx',
        ]),

        async initPage() {
            this.loading = true;

            this.fullResetUserStore();

            if (this.userID) {
                try {
                    const promisesAll = await Promise.all([
                        this.loadFullUserById(this.userID),
                        this.loadActivityTimeUserById(this.userID),
                        this.checkFriend({ userId: this.userID, store: true }),
                    ]);
                    this.lastUserActivityTime = promisesAll[1];
                } catch (e) {
                    console.error(e);
                }
            }

            const routeQuery = this.$route.query;
            const queryString = Object.keys(routeQuery)
                .map((key) => `${key}=${routeQuery[key]}`)
                .join('&');
            const queryData = qs.parse(queryString) || {};

            if (queryData.filters && Object.keys(queryData.filters).length > 0) {
                const filters = queryData.filters;

                if (filters.profile) {
                    if (filters.profile.withDrafts) {
                        filters.profile.withDrafts = filters.profile.withDrafts === 'true';
                    }
                    if (filters.profile.importantFieldsFilled) {
                        filters.profile.importantFieldsFilled = filters.profile.importantFieldsFilled === 'true';
                    }
                }

                if (filters.favorite_membership) {
                    filters.favorite_membership.membership_owner_id = filters.favorite_membership.membership_owner_id
                        ? parseInt(filters.favorite_membership.membership_owner_id)
                        : null;
                    filters.favorite_membership.isFavorite = filters.favorite_membership.isFavorite === 'true';
                    filters.favorite_membership.status = filters.favorite_membership.status === 'true';
                }

                this.filters = filters;
            }

            if (queryData.search) {
                this.searchString = queryData.search;
            }

            if (queryData.offset) {
                const offset = parseInt(queryData.offset);
                this.offset = offset;
                this.skip = offset > 0 ? offset - 1 : 0;
            }

            this.$nextTick(() => {
                this.$apollo.skipAll = false;

                this.$apollo.queries.users.refetch({
                    searchString: this.searchString,
                    skip: this.skip,
                });
            });

            this.loading = false;
        },
        async download(type) {
            let resp;
            let fileName = '';
            let loader;

            const fio = [this.getUser.surname, this.getUser.name, this.getUser.patronymic].filter((s) => s).join(' ');

            if (type === 'pdf') {
                loader = 'pdf';
                this.downloading = { [loader]: true };
                resp = await this.downloadPdf();
                fileName = `Портфолио ${fio}.pdf`;
            } else {
                loader = 'docx';
                this.downloading = { [loader]: true };
                resp = await this.downloadDocx();
                fileName = `Портфолио ${fio}.docx`;
            }

            this.downloading = { [loader]: false };
            const file = window.URL.createObjectURL(new Blob([resp]));
            const a = document.createElement('a');

            a.href = file;
            a.setAttribute('download', fileName);
            document.documentElement.appendChild(a);
            a.click();
            a.remove();
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~@/styles/rem';
@import '~@/styles/variables';

@mixin border-radius {
    border-radius: mrem(16);

    @include on-desktop {
        border-radius: rem(16);
    }
}

.user-portfolio {
    background: #fff;
    font-family: $font-montserrat;
    font-style: normal;
    font-weight: 400;
    color: #2a2626;
    font-size: mrem(16);
    line-height: mrem(22);
    padding: 0 mrem(10) mrem(60) mrem(10);

    @include on-desktop {
        font-size: rem(16);
        line-height: rem(22);
        padding: 0 rem(10) rem(60) rem(10);
    }

    &__back {
        margin-bottom: rem(16);

        &-link {
            color: #98a0a9;
            font-style: normal;
            font-weight: 600;
            display: flex;
            flex-direction: row;
            gap: mrem(6);
            font-size: mrem(14);
            line-height: mrem(24);

            @include on-desktop {
                gap: rem(6);
                font-size: rem(14);
                line-height: rem(24);
            }

            &:before {
                display: inline-block;
                content: '';
                background: url('~@/assets/images/portfolio/back.svg') no-repeat center center;
                background-size: cover;
                width: mrem(24);
                height: mrem(24);

                @include on-desktop {
                    width: rem(24);
                    height: rem(24);
                }
            }
        }
    }

    &__wrapper {
        width: 88%;
        position: relative;
        min-height: 100%;
        margin: 0 auto;

        @include on-mobile {
            width: 100%;
            margin: 0 0 mrem(8);
        }
    }
    &__loader {
        position: fixed;
        top: 50%;
        left: 50%;
        display: flex;
        transform: translate(-50%, -50%);
    }
    &__content {
        padding-top: mrem(50);

        @include on-desktop {
            padding-top: rem(50);
        }

        .title {
            margin: 0 0 mrem(50);
            display: flex;
            flex-direction: column;
            gap: mrem(40);
            justify-content: space-between;

            background: $color-main-blue;
            box-shadow: none;
            color: #fff;
            font-weight: 700;
            font-style: normal;
            padding: mrem(16) mrem(40);
            font-size: mrem(21);
            line-height: mrem(30);
            border-radius: mrem(16);

            @include on-desktop {
                padding: rem(16) rem(40);
                font-size: rem(21);
                line-height: rem(30);
                border-radius: rem(16);
                gap: rem(40);
                flex-direction: row;
                margin: 0 0 rem(50);
            }

            &__time {
                font-weight: 500;
                font-size: mrem(16);
                line-height: mrem(22);
                @include on-desktop {
                    font-size: rem(16);
                    line-height: rem(22);
                }
            }

            &__text {
                margin: 0;
                font-size: mrem(21);
                line-height: mrem(30);
                font-weight: 700;

                @include on-desktop {
                    font-size: rem(21);
                    line-height: rem(30);
                }
            }

            &__block {
                display: flex;
                flex-direction: column;
                align-items: center;
                text-align: center;
                gap: rem(40);

                @include on-desktop {
                    gap: rem(40);
                    flex-direction: row;
                    align-items: center;
                    text-align: left;
                }
            }

            .download {
                font-weight: 500;
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: mrem(6);
                margin-top: mrem(10);
                font-size: mrem(16);
                line-height: mrem(22);
                color: #fff;

                @include on-desktop {
                    margin-top: 0;
                    gap: rem(6);
                    font-size: rem(16);
                    line-height: rem(22);
                }

                &::after {
                    content: '';
                    display: inline-block;
                    background: url('~@/assets/images/portfolio/download.svg') no-repeat center center;
                    background-size: cover;
                    margin-top: mrem(-5);
                    width: mrem(24);
                    height: mrem(24);

                    @include on-desktop {
                        width: rem(24);
                        height: rem(24);
                        margin-top: rem(-5);
                    }
                }
            }

            .portfolio-link {
                display: flex;
                justify-content: center;
                align-items: center;
                height: mrem(42);
                padding: mrem(8) mrem(32);
                gap: mrem(6);
                border-radius: mrem(10);
                border: mrem(1.6) solid #fff;
                color: #fff;
                text-align: center;
                font-weight: 700;
                font-size: mrem(14);
                line-height: mrem(20);

                @include on-desktop {
                    height: rem(42);
                    padding: rem(8) rem(32);
                    gap: rem(6);
                    border-radius: rem(10);
                    border-width: rem(1.6);
                    font-size: rem(14);
                    line-height: rem(20);
                }

                &::after {
                    content: '';
                    display: inline-block;
                    background: url('~@/assets/images/portfolio/arrow.svg') no-repeat center center;
                    background-size: cover;
                    margin-top: mrem(-2);
                    width: mrem(24);
                    height: mrem(24);

                    @include on-desktop {
                        width: rem(24);
                        height: rem(24);
                        margin-top: rem(-2);
                    }
                }

                &--prev {
                    &::after {
                        order: -1;
                        transform: rotate(180deg);
                    }
                }
                &--next {
                }
            }
        }
    }

    &__menu {
        border-radius: mrem(16);
        background: #fff;
        box-shadow: 0 mrem(2) mrem(16) 0 rgba(70, 99, 171, 0.1);
        padding: mrem(16);
        display: flex;
        flex-direction: column;
        gap: mrem(16);
        @include on-desktop {
            border-radius: rem(16);
            box-shadow: 0 rem(2) rem(16) 0 rgba(70, 99, 171, 0.1);
            padding: rem(16);
            gap: rem(16);
        }

        & > div {
            cursor: pointer;
        }
    }
}
:deep(.fade-enter-active),
:deep(.fade-leave-active) {
    transition: opacity 0.5s;
}
:deep(.fade-enter),
:deep(.fade-leave-to) {
    opacity: 0;
}

.user-portfolio-columns {
    display: flex;
    flex-direction: column;
    gap: mrem(40);

    @include on-desktop {
        display: grid;
        grid-template-columns: 716fr 780fr;
        gap: rem(73);
    }

    &--inner {
        grid-template-columns: 466fr 218fr;
        gap: mrem(32);

        @include on-desktop {
            gap: rem(32);
        }
    }

    .user-portfolio-column {
        display: flex;
        flex-direction: column;
        gap: mrem(40);

        @include on-desktop {
            gap: rem(40);
        }

        .user-portfolio-block {
            @include border-radius;
            box-shadow: 0 0 mrem(16) rgba(70, 99, 171, 0.2);
            padding: mrem(20);

            @include on-desktop {
                box-shadow: 0 0 rem(16) rgba(70, 99, 171, 0.2);
                padding: rem(20);
            }

            &--wide-padding {
                padding: mrem(40);

                @include on-desktop {
                    padding: rem(40);
                }
            }

            &--blue {
                background-color: $color-main-notice-lighter;
            }

            &--borderless {
                box-shadow: none;
                border: none;
                border-radius: 0;
                padding-left: 0;
                padding-right: 0;
                padding-top: mrem(40);
                border-top: mrem(2) solid #f8f8f8;

                @include on-desktop {
                    padding-top: rem(40);
                    border-top: rem(2) solid #f8f8f8;
                }
            }
        }
    }
}

:deep(span.highlight) {
    background: $color-search-highlight-background;
}
</style>
